import React, { useEffect, useState } from "react";
import ArrayFieldSection from "./ArrayFieldSection";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "react-toastify";

// Fix Leaflet marker icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().split("T")[0];
};

const EditFieldRenderer = ({
  field,
  formData,
  handleInputChange,
  handleDropdownChange,
  handleArrayChange,
  handleAddItem,
  handleRemoveItem,
  dropdownData,
  setFormData,
}) => {
  const rawValue = formData[field.fields] || "";
  const value =
    field.type === "date" && rawValue ? formatDate(rawValue) : rawValue;
  const isReadOnly = field.edit === "no";

  const rawLocation =
    typeof formData[field.fields] === "string"
      ? formData[field.fields].split(",")
      : ["", ""];

  const [lat, setLat] = useState(parseFloat(rawLocation[0]) || 20.5937);
  const [lng, setLng] = useState(parseFloat(rawLocation[1]) || 78.9629);
  const [locationFetched, setLocationFetched] = useState(false);

  const isValidLatLng = !isNaN(lat) && !isNaN(lng);
  const latLng = isValidLatLng ? [lat, lng] : [20.5937, 78.9629];

  // 🌍 Fetch geolocation only for location fields
  useEffect(() => {
    if (field.type !== "location" || locationFetched || isValidLatLng) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLat(latitude);
        setLng(longitude);
        setFormData((prev) => ({
          ...prev,
          [field.fields]: `${latitude},${longitude}`,
        }));
        setLocationFetched(true);
      },
      (err) => {
        toast.error("Unable to fetch current location.");
        console.error("Geolocation error:", err);
      },
      { enableHighAccuracy: true }
    );
  }, [field, isValidLatLng, locationFetched, setFormData]);

  // 🌐 Fallback for array values
  const valueArray = Array.isArray(formData[field.fields])
    ? formData[field.fields]
    : formData[field.fields]
    ? [formData[field.fields]]
    : [];

  if (field.subfields) {
    return (
      <ArrayFieldSection
        field={field}
        formData={formData}
        handleArrayChange={handleArrayChange}
        handleAddItem={handleAddItem}
        handleRemoveItem={handleRemoveItem}
        dropdownData={dropdownData}
      />
    );
  }

if (field.type === "array" && Array.isArray(field.checkBox)) {
  const selected = Array.isArray(formData[field.fields]) ? formData[field.fields] : [];

  const toggleCheckbox = (item) => {
    const updated = selected.includes(item)
      ? selected.filter((i) => i !== item)
      : [...selected, item];

    handleInputChange({
      target: {
        name: field.fields,
        value: updated,
      },
    });
  };

  const allSelected = field.checkBox.every((item) => selected.includes(item));

  const toggleSelectAll = () => {
    handleInputChange({
      target: {
        name: field.fields,
        value: allSelected ? [] : field.checkBox,
      },
    });
  };

  return (
    <div className="form-group">
      <label className="form-label">{field.title || field.fields}</label>

      <div className="select-all-container">
        <label>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            disabled={isReadOnly}
          />
          Select All
        </label>
      </div>

      <div className="checkbox-grid">
        {field.checkBox.map((item) => (
          <label key={item} className="checkbox-item">
            <input
              type="checkbox"
              checked={selected.includes(item)}
              onChange={() => toggleCheckbox(item)}
              disabled={isReadOnly}
            />
            {item}
          </label>
        ))}
      </div>
    </div>
  );
}



  return (
    <div className="form-group">
      <label className="form-label">{field.fields}</label>

      {field.api && field.type === "ObjectId" && field.multiple ? (
        <div className="multi-select-section">
          <select
            className="form-select"
            value=""
            onChange={(e) => {
              const selected = e.target.value;
              if (!selected) return;
              const current = formData[field.fields] || [];
              if (!current.includes(selected)) {
                handleInputChange({
                  target: {
                    name: field.fields,
                    value: [...current, selected],
                  },
                });
              }
            }}
            disabled={isReadOnly}
          >
            <option value="">Select {field.fields}</option>
            {dropdownData[field.fields]
              ?.filter(
                (opt) => !(formData[field.fields] || []).includes(opt.id)
              )
              .map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
          </select>

          <div className="selected-bubbles">
            {valueArray.map((selectedId, index) => {
              const selectedItem = dropdownData[field.fields]?.find(
                (i) => i.id === selectedId
              );
              return (
                <span key={index} className="bubble">
                  {selectedItem?.label ?? `Unknown (${selectedId})`}
                  <button
                    type="button"
                    className="remove-bubble"
                    onClick={() => {
                      const updated = valueArray.filter((_, i) => i !== index);
                      handleInputChange({
                        target: {
                          name: field.fields,
                          value: updated,
                        },
                      });
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      ) : field.api && field.type === "ObjectId" ? (
        <select
          className="form-select"
          name={field.fields}
          value={value}
          onChange={handleDropdownChange}
          disabled={isReadOnly}
        >
          <option value="">Select {field.fields}</option>
          {dropdownData[field.fields]?.map((item) => (
            <option key={item.id || item._id} value={item.id || item._id}>
              {item.label || item[field.apiField]}
            </option>
          ))}
        </select>
      ) : field.options ? (
        <select
          className="form-select"
          name={field.fields}
          value={value}
          onChange={handleInputChange}
          disabled={isReadOnly}
        >
          <option value="">Select {field.fields}</option>
          {field.options.map((opt, idx) => (
            <option key={idx} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : field.type === "number" ? (
        <input
          className="form-input"
          type="number"
          name={field.fields}
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            if (/^\d*$/.test(newValue)) {
              handleInputChange(e);
            }
          }}
        />
      ) : field.type === "date" ? (
        <input
          className="form-input"
          type="date"
          name={field.fields}
          value={value}
          onChange={handleInputChange}
        />
      ) : field.type === "location" ? (
        <div className="location-section">
          <label>{field.fields} (📍 Location)</label>
          {isValidLatLng && (
            <p>
              <strong>Saved Location:</strong> {rawLocation.join(", ")}
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  const current = `${pos.coords.latitude},${pos.coords.longitude}`;
                  const use = window.confirm(
                    `Use current location?\nCurrent: ${current}\nSaved: ${rawLocation.join(
                      ", "
                    )}`
                  );
                  if (use) {
                    setFormData((prev) => ({
                      ...prev,
                      [field.fields]: current,
                    }));
                    setLat(pos.coords.latitude);
                    setLng(pos.coords.longitude);
                    toast.success("✅ Location updated.");
                  }
                },
                (err) => {
                  toast.error("⚠️ Failed to fetch current location.");
                  console.error("Geolocation Error:", err);
                },
                { enableHighAccuracy: true }
              );
            }}
            className="set-location-btn"
          >
            📍 Use My Current Location
          </button>

          <MapContainer
            center={latLng}
            zoom={16}
            scrollWheelZoom={false}
            style={{ height: "250px", width: "100%", marginTop: "10px" }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={latLng}>
              <Popup>
                {isValidLatLng ? "Displayed Location" : "Your Current Location"}
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      ) : (
        <input
          className="form-input"
          type="text"
          name={field.fields}
          value={value}
          onChange={handleInputChange}
          readOnly={isReadOnly}
          disabled={isReadOnly}
        />
      )}
    </div>
  );
};

export default EditFieldRenderer;
